/**
 * Outings Page - Main listing page for golf outings
 * Entry point for outing management module.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, Users, DollarSign, MapPin, ChevronRight, Trophy } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useOutingApi } from '../hooks/useOutingApi';
import { STATUS_CONFIG, formatDate, formatCurrency } from '../utils/outingHelpers';
import OutingCreateFlow from '../components/OutingCreateFlow';

export default function OutingsPage({ user }) {
  const navigate = useNavigate();
  const api = useOutingApi();
  const [outings, setOutings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateFlow, setShowCreateFlow] = useState(false);

  useEffect(() => {
    loadOutings();
  }, []);

  const loadOutings = async () => {
    setLoading(true);
    try {
      const data = await api.listOutings({ limit: 50 });
      if (data.success) {
        setOutings(data.outings);
      }
    } catch (err) {
      console.error('Failed to load outings:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredOutings = outings.filter(o => {
    if (activeTab === 'organizing') return o.organizer_id === user?.user_id;
    if (activeTab === 'upcoming') return ['open', 'locked', 'in_progress'].includes(o.status);
    if (activeTab === 'completed') return o.status === 'completed';
    return true;
  });

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return (
      <Badge variant="outline" className={`text-${config.color}-400 border-${config.color}-400/50`}>
        {config.icon} {config.label}
      </Badge>
    );
  };

  return (
    <div className="ps-theme-page min-h-screen p-4 pb-24" data-testid="outings-theme-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase">Golf Outings</h1>
          <p className="text-zinc-400 text-sm">Manage tournaments and group events</p>
        </div>
        <Button
          onClick={() => setShowCreateFlow(true)}
          className="bg-[#007AFF] hover:bg-[#38BDF8] text-white font-bold"
          data-testid="create-outing-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Outing
        </Button>
      </div>

      {/* Popup Create Flow */}
      <OutingCreateFlow 
        isOpen={showCreateFlow} 
        onClose={() => {
          setShowCreateFlow(false);
          loadOutings(); // Refresh list after creation
        }}
        user={user}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#007AFF]/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#007AFF]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{outings.length}</p>
                <p className="text-xs text-zinc-400">Total Outings</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {outings.filter(o => o.status === 'in_progress').length}
                </p>
                <p className="text-xs text-zinc-400">Active Now</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {outings.filter(o => o.organizer_id === user?.user_id).length}
                </p>
                <p className="text-xs text-zinc-400">You Organize</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {formatCurrency(outings.reduce((sum, o) => sum + (o.total_collected || 0), 0))}
                </p>
                <p className="text-xs text-zinc-400">Collected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-[#1A1A1A] border border-white/10">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#007AFF]">All</TabsTrigger>
          <TabsTrigger value="organizing" className="data-[state=active]:bg-[#007AFF]">Organizing</TabsTrigger>
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-[#007AFF]">Upcoming</TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-[#007AFF]">Completed</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Outings List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full" />
        </div>
      ) : filteredOutings.length === 0 ? (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-8 text-center">
            <Trophy className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Outings Yet</h3>
            <p className="text-zinc-400 mb-4">Create your first golf outing to get started!</p>
            <Button
              onClick={() => navigate('/outings/create')}
              className="bg-[#007AFF] hover:bg-[#38BDF8]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Outing
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOutings.map(outing => (
            <Card
              key={outing.id}
              className="bg-[#121212] border-white/10 hover:border-[#007AFF]/50 transition-colors cursor-pointer"
              onClick={() => navigate(`/outings/${outing.id}`)}
              data-testid={`outing-card-${outing.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold">{outing.name}</h3>
                      {getStatusBadge(outing.status)}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(outing.event_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {outing.course_name || 'TBD'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {outing.total_accepted || 0}/{outing.max_players} players
                      </span>
                      {outing.entry_fee > 0 && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {formatCurrency(outing.entry_fee)} entry
                        </span>
                      )}
                    </div>
                    
                    {outing.organizer_id === user?.user_id && (
                      <div className="mt-3 flex gap-4 text-xs">
                        <span className="text-green-400">
                          ${outing.total_collected || 0} collected
                        </span>
                        {outing.total_outstanding > 0 && (
                          <span className="text-red-400">
                            ${outing.total_outstanding} outstanding
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-zinc-500" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
